// "Appareils" — my devices: who is online, link a new one (QR), revoke one, export / import,
// leave the space on this device.

import { getConversations, getSpace, setSpace } from '../space/current';
import { LinkHost, deviceCode, type LinkRequest } from '../space/link';
import { readNotdZip } from '../notes/notd';
import { saveBackup } from '../notes/backup';
import { randomHex } from '../core/ids';
import type { Space } from '../space/space';
import { h, keep } from './dom';
import { canShareFiles, copyText, iceServers, keepAwake, qrElement, saveFile, shareFiles, wsUrl } from './common';
import { relativeDate } from './notes';
import type { View } from './view';

// Relay mode survives screen changes: it belongs to the page, not to this view.
let relayRelease: (() => void) | null = null;

export function devicesView(root: HTMLElement): View {
  let space: Space | null = null;
  let link: { host: LinkHost; url: string; qr: HTMLElement; request: LinkRequest | null; done: string | null; release: () => void } | null = null;
  let message: { text: string; bad?: boolean } | null = null;
  let busy = false;
  let persisted = false;
  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  };

  async function startLink() {
    if (!space) return;
    const host = new LinkHost(space);
    const url = host.link(location.origin);
    // iOS suspends a backgrounded or locked app: the QR code would stay on screen with nobody
    // answering behind it. Keep the screen on while linking.
    link = { host, url, qr: qrElement(url), request: null, done: null, release: keepAwake() };
    host.addEventListener('request', (e) => {
      link!.request = (e as CustomEvent).detail;
      render();
    });
    host.addEventListener('done', (e) => {
      link!.done = (e as CustomEvent).detail.device.name;
      link!.request = null;
      render();
    });
    host.addEventListener('expired', () => {
      link?.release();
      link = null;
      message = { text: 'Le QR code a expiré (10 min). Recommence pour en obtenir un nouveau.' };
      render();
    });
    render();
    await host.start(wsUrl, await iceServers());
  }

  function stopLink() {
    link?.release();
    link?.host.stop();
    link = null;
    render();
  }

  async function exportNotes() {
    if (!space) return;
    await space.notes.flush();
    const n = await saveBackup(space.store, saveFile, shareFiles, canShareFiles);
    message = { text: `${n} note${n > 1 ? 's' : ''} exportée${n > 1 ? 's' : ''}.` };
    render();
  }

  async function importNotes(file: File) {
    if (!space) return;
    busy = true;
    message = { text: 'Import en cours…' };
    render();
    try {
      const imported = readNotdZip(new Uint8Array(await file.arrayBuffer()));
      const byName = new Map(space.notes.activeFolders().map((f) => [f.name, f.id]));
      let i = 0;
      for (const n of imported) {
        let folder: string | null = null;
        if (n.folder) {
          folder = byName.get(n.folder) ?? null;
          if (!folder) {
            folder = randomHex(8);
            byName.set(n.folder, folder);
            await space.notes.setFolder({ id: folder, name: n.folder.slice(0, 60), color: null, position: byName.size });
          }
        }
        await space.notes.create({ ...n.fields, folder }, n.body);
        if (++i % 25 === 0) {
          message = { text: `Import en cours… ${i} / ${imported.length}` };
          render();
        }
      }
      message = { text: `${imported.length} note${imported.length > 1 ? 's' : ''} importée${imported.length > 1 ? 's' : ''}.` };
    } catch (e) {
      message = { text: `Import impossible : ${(e as Error).message}`, bad: true };
    }
    busy = false;
    render();
  }

  async function revoke(id: string, name: string) {
    if (!space || !confirm(`Retirer « ${name} » de ton espace ? Il ne recevra plus rien (il garde ce qu’il a déjà).`)) return;
    await space.revoke(id);
    message = { text: `« ${name} » a été retiré. Une nouvelle clé a été partagée avec tes autres appareils.` };
    render();
  }

  async function leave() {
    if (!space) return;
    const only = space.journal.activeMembers().length < 2;
    if (confirm('Exporter d’abord une sauvegarde de tes notes ? (fortement conseillé)')) await exportNotes();
    const warning = only
      ? 'C’est ton SEUL appareil : tes notes et tes discussions seront définitivement perdues.'
      : 'Tes notes et discussions seront effacées de CET appareil (tes autres appareils les gardent).';
    if (prompt(`${warning}\nTape EFFACER pour confirmer.`)?.trim().toUpperCase() !== 'EFFACER') return;
    await (await getConversations())?.deleteAll();
    await space.leave();
    setSpace(null);
    location.hash = '#/notes';
  }

  function linkCard(): Node {
    if (!link) {
      return h('button', { class: 'btn primary', text: 'Lier un appareil', onclick: () => void startLink() });
    }
    const l = link;
    if (l.done) {
      return h('section', { class: 'card' }, h('p', { class: 'ok', text: `« ${l.done} » est lié. Il se synchronise maintenant.` }), h('button', { class: 'btn small', text: 'Fermer', onclick: stopLink }));
    }
    if (l.request) {
      const r = l.request;
      return h(
        'section',
        { class: 'card' },
        h('p', { text: `Ajouter « ${r.device.name} » à ton espace ?` }),
        h('p', { class: 'dim', text: 'Vérifie que ce code est bien celui affiché sur l’autre appareil :' }),
        h('p', { class: 'code', text: deviceCode(r.device.id) }),
        h('div', { class: 'row' }, h('button', { class: 'btn primary small', text: 'Ajouter', onclick: () => void l.host.accept() }), h('button', { class: 'btn small', text: 'Refuser', onclick: () => (l.host.decline(), (l.request = null), render()) })),
      );
    }
    const copy = h('button', { class: 'btn small', text: 'Copier le lien', onclick: async () => (copy.textContent = (await copyText(l.url)) ? 'Copié' : 'Copie impossible') });
    return h(
      'section',
      { class: 'card' },
      h('h2', { text: 'Lier un appareil' }),
      h('p', { class: 'dim', text: 'Scanne ce QR code avec l’autre appareil (appareil photo), ou ouvre-y le lien. Valable 10 minutes.' }),
      h('p', { class: 'warn small', text: 'Garde cet écran ouvert pendant la liaison : si l’app passe en arrière-plan, l’autre appareil ne pourra pas te joindre.' }),
      l.qr,
      h('input', { class: 'field link-url', readonly: true, value: l.url, onfocus: (e: Event) => (e.target as HTMLInputElement).select() }),
      h('div', { class: 'row' }, copy, h('button', { class: 'btn small', text: 'Annuler', onclick: stopLink })),
    );
  }

  function render() {
    if (!space) {
      root.replaceChildren(h('h1', { text: 'Appareils' }), h('p', { class: 'dim' }, 'Pas encore d’espace sur cet appareil. ', h('a', { href: '#/notes', text: 'Créer ou rejoindre un espace' })));
      return;
    }
    const s = space;
    const online = s.sync?.onlineDevices() ?? new Set<string>();
    const members = [...s.journal.members.values()].sort((a, b) => a.addedAt - b.addedAt);
    const fileInput = h('input', { type: 'file', accept: '.zip,application/zip', hidden: true, onchange: () => fileInput.files?.[0] && void importNotes(fileInput.files[0]) });
    root.replaceChildren(
      ...keep(
        h('div', { class: 'row' }, h('a', { class: 'btn small', href: '#/notes', text: '← Notes' }), h('h1', { text: 'Appareils' })),
        message && h('p', { class: message.bad ? 'bad' : 'ok', text: message.text }),
        h(
          'ul',
          { class: 'files' },
          ...members.map((m) => {
            const me = m.record.id === s.device.id;
            const revoked = m.revokedAfter !== undefined;
            const status = me ? 'cet appareil' : revoked ? 'retiré' : online.has(m.record.id) ? 'en ligne' : 'hors ligne';
            return h(
              'li',
              { class: 'device' },
              h('div', { class: 'file-head' }, h('span', { class: 'file-name', text: m.record.name }), h('span', { class: status === 'en ligne' ? 'ok' : 'dim', text: status })),
              h('div', { class: 'dim small', text: `code ${deviceCode(m.record.id)} · ajouté ${relativeDate(m.addedAt)}` }),
              !me && !revoked && h('button', { class: 'btn small', text: 'Retirer', onclick: () => void revoke(m.record.id, m.record.name) }),
            );
          }),
        ),
        linkCard(),
        h('h2', { text: 'Sauvegarde' }),
        h('p', { class: persisted ? 'ok small' : 'warn small', text: persisted ? 'Stockage protégé par le navigateur.' : 'Stockage non garanti par le navigateur : installe l’app (écran d’accueil / Dock) et sauvegarde régulièrement.' }),
        h('p', { class: 'dim small', text: 'Format notd : des fichiers Markdown dans un .zip, réimportables dans notd.' }),
        h('div', { class: 'row' }, h('button', { class: 'btn small', text: 'Exporter mes notes (.zip)', disabled: busy, onclick: () => void exportNotes() }), fileInput, h('button', { class: 'btn small', text: 'Importer un export notd', disabled: busy, onclick: () => fileInput.click() })),
        h('h2', { text: 'Cet appareil' }),
        h('p', { class: 'dim small', text: 'Mode relais : tant que l’app reste ouverte ici, cet appareil fait passer les messages et les notes entre tes autres appareils et tes contacts. Idéal pour un ordinateur ou un vieux téléphone sur secteur.' }),
        h('button', {
          class: `btn small${relayRelease ? ' primary' : ''}`,
          text: relayRelease ? 'Mode relais activé (écran maintenu allumé)' : 'Activer le mode relais',
          onclick: () => {
            if (relayRelease) {
              relayRelease();
              relayRelease = null;
            } else relayRelease = keepAwake();
            render();
          },
        }),
        h('button', { class: 'btn danger small', text: 'Quitter l’espace sur cet appareil', onclick: () => void leave() }),
      ),
    );
  }

  root.replaceChildren(h('p', { class: 'dim', text: 'Ouverture…' }));
  void navigator.storage?.persisted?.().then((p) => ((persisted = p), render())).catch(() => {});
  void getSpace().then((s) => {
    space = s;
    s?.sync?.addEventListener('peers', schedule);
    s?.journal.addEventListener('envelope', schedule);
    render();
  });

  return {
    busy: () => busy,
    destroy() {
      cancelAnimationFrame(frame);
      link?.release();
      link?.host.stop();
      space?.sync?.removeEventListener('peers', schedule);
      space?.journal.removeEventListener('envelope', schedule);
    },
  };
}
